import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  inject,
  Inject,
  OnInit,
} from '@angular/core';
import { OfferFacade } from '@features/offer/facades';
import { OfferHelper } from '@features/offer/helpers';
import { Breakpoints } from '@rd-lib/breakpoints';
import { AnalyticsService } from '@rd/core/services/analytics/analytics.service';
import { IconRegistryService } from '@rd/core/services/icon-registry/icon-registry.service';
import { PageTitleService } from '@rd/core/services/page-title/page-title.service';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { WindowRefService } from '@rd/core/services/window-ref/window-ref.service';
import { WindowResizeService } from '@rd/core/services/window-resize/window-resize.service';
import { TENANT_NAME } from '@rd/core/tokens/rd-lib.token';
import {
  detectViewPort,
  responsiveBackgroundImage,
  ViewPortType,
} from '@rd/helpers/responsive-image.helper';
import * as CSS from 'csstype';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { WestpacRedirectService } from '../../services/redirect.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'rd-westpac-home-page',
  templateUrl: './home-page.component.html',
  styleUrls: ['./home-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WestpacHomePageComponent implements OnInit {
  @HostBinding('class.rd-westpac-home-page') componentClass = true;

  specialOffersBgImage: CSS.Properties;
  hotelsBgImage: CSS.Properties;
  giftCardsBgImage: CSS.Properties;
  viewPortType: ViewPortType;
  travelEdgeUrl: URL;
  showSubtitle: boolean;

  private destroy$ = new Subject();
  private offerFacade = inject(OfferFacade);

  recommendedOffers$ = this.offerFacade.offerListItems$.pipe(
    distinctUntilChanged(),
  );
  recommendedOffersStatus$ = this.offerFacade.offerRenderedState$.pipe(
    distinctUntilChanged(),
  );

  constructor(
    @Inject(TENANT_NAME) private tenantName: string,
    private cdRef: ChangeDetectorRef,
    private iconRegistryService: IconRegistryService,
    private redirectService: WestpacRedirectService,
    private urlUtilsService: UrlUtilsService,
    private windowResizeService: WindowResizeService,
    private windowRefService: WindowRefService,
    private pageTitleService: PageTitleService,
    private offerHelper: OfferHelper,
    private analyticsService: AnalyticsService,
  ) {}

  ngOnInit(): void {
    this.registerIcons();
    this.loadRecommendedOffers();
    this.travelEdgeUrl = this.urlUtilsService.travelEdgeUrl();

    this.detectViewPort();
    this.windowResizeService.windowResizeObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.detectViewPort();
      });
    this.pageTitleService.setPageTitle('home-page');
    this.analyticsService.track({
      routeName: 'home',
    });
  }

  loadRecommendedOffers(): void {
    // get 3 firts offers
    this.offerFacade.loadPaginatedOffers({
      queryParams: {
        sort: '-score_by_recommended',
      },
      paginationOptions: {
        page: 1,
        pageSize: 3,
      },
    });
  }

  bannerClass(): string {
    return `banner relative banner-${this.tenantName}`;
  }

  setBackgroundImageStyle(): void {
    this.specialOffersBgImage = this.setResponsiveBackgroundImage(
      'homepage-special-offers',
    );
    this.hotelsBgImage = this.setResponsiveBackgroundImage('homepage-hotels');
    this.giftCardsBgImage = this.setResponsiveBackgroundImage(
      'homepage-gift-cards',
    );
    this.cdRef.markForCheck();
  }

  private detectViewPort(): void {
    this.showSubtitle = window.innerWidth > Breakpoints.TABLET;
    detectViewPort(
      this.cdRef,
      this.viewPortType,
      this.windowRefService.nativeWindow.innerWidth,
      (viewPortType: ViewPortType) => {
        this.viewPortType = viewPortType;
        this.setBackgroundImageStyle();
      },
    );
  }

  private setResponsiveBackgroundImage(rootImageName: string): CSS.Properties {
    return responsiveBackgroundImage({
      devicePixelRatio: this.windowRefService.nativeWindow.devicePixelRatio,
      viewPortType: this.viewPortType,
      srcset: {
        mobile: this.urlUtilsService.imageUrl(
          `/images/${rootImageName}-mobile.jpg`,
        ),
        mobile2x: this.urlUtilsService.imageUrl(
          `/images/${rootImageName}-mobile.jpg`,
        ),
        tablet: this.urlUtilsService.imageUrl(
          `/images/${rootImageName}-mobile.jpg`,
        ),
        tablet2x: this.urlUtilsService.imageUrl(
          `/images/${rootImageName}-mobile.jpg`,
        ),
        desktop: this.urlUtilsService.imageUrl(
          `/images/${rootImageName}-desktop.jpg`,
        ),
        desktop2x: this.urlUtilsService.imageUrl(
          `/images/${rootImageName}-desktop.jpg`,
        ),
      },
    });
  }

  private registerIcons(): void {
    this.iconRegistryService.registerIcon('chevron-right');
  }

  getOfferDetailsPageUrl(id: string): string {
    return this.offerHelper.detailsPageUrl(id);
  }

  getOfferListingPageUrl(): string {
    return this.offerHelper.listingPageUrl();
  }
}
